import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDesignSystem } from "../context/designSystemContext";
import { Categories } from "../types/categories";
import {
  aggregateSubcategoryMetrics,
  normalizeRiskMetrics,
} from "../utils/riskControlMetrics";
import { pSBC } from "../utils/pSBC";
import {
  IndicatorMetricsBlock,
  IndicatorRiskMetricsRow,
} from "./indicatorMetricBox";

type IndicatorsFilterListType = {
    riskData: Categories[],
    priorityFilter: string[],
}

export const IndicatorsFilterList: React.FC<IndicatorsFilterListType> = ({ riskData, priorityFilter }) => {
    const [groupedData, setGroupedData] = useState<Record<string, Record<string, Categories[]>>>({});

    const { designSystem } = useDesignSystem();
    const primaryColor = designSystem?.cssBasics.primaryColor ?? "#005030";
    
    useEffect(() => {
        const groupedByCategory = riskData ? riskData.reduce((acc: Record<string, Record<string, Categories[]>>, item) => {
            if (!acc[item.category]) {
                acc[item.category] = {};
            }
            if (!acc[item.category][item.subcategory]) {
                acc[item.category][item.subcategory] = [];
            }
            acc[item.category][item.subcategory].push(item);
            return acc;
        }, {}) : {};
        
        setGroupedData(groupedByCategory);
    }, [riskData, priorityFilter]);

    const organizeGridCols = (length: number) => {
        switch (length) {
            case 1:
                return `grid-cols-1`
            case 2:
                return `grid-cols-2`
            default:
                return "grid-cols-3"
        }
    }

    const organizeBasis = (length: number) => {
        switch (length) {
            case 1:
                return 'basis-1/3'
            case 2:
                return `basis-2/3`
            default:
                return "w-full"
        }
    }

    const organizeLateralColor = (priority: string) => {
        switch (priority) {
            case "HIGH":
                return 'bg-[#FF9999]'
            case "REGULAR":
                return 'bg-[#FFE999]'
            case "LOW":
                return 'bg-[#99FFCE]'
            default:
                return 'bg-gray-200'
        }
    }

    return (
        <div className="flex flex-wrap p-2">

            {Object.keys(groupedData).map((category) => (
                <div key={category} className={`flex ${organizeBasis(Object.keys(groupedData[category]).length)} pb-2 px-1 min-w-0`}>
                    <div className="w-full min-w-0">
                        <h2 className={`font-openSans font-bold text-white text-left pl-3 py-1 rounded-md text-[15px] leading-[30px] tracking-[0.2px] mb-2`} style={{
                            backgroundColor: primaryColor,
                        }}>
                            {category}
                        </h2>

                        <div className={`grid ${organizeGridCols(Object.keys(groupedData[category]).length)} gap-2`}>
                            {Object.keys(groupedData[category]).map((subcategory) => {
                                const risks = groupedData[category][subcategory];
                                const subcategoryMetrics = aggregateSubcategoryMetrics(risks);

                                return (
                                <div className='bg-white shadow-md rounded-md px-2 pb-2 pt-2 m-1 flex flex-col min-w-0' key={subcategory}>
                                    <h3
                                        className="text-left font-openSans font-bold text-[13px] leading-snug tracking-[0.2px] mb-2 px-1 truncate"
                                        style={{ color: pSBC(0.9, primaryColor) ?? primaryColor }}
                                    >
                                        {subcategory}
                                    </h3>

                                    <IndicatorMetricsBlock
                                        metrics={subcategoryMetrics}
                                        accentColor={primaryColor}
                                    >
                                        {risks.map((risk) => (
                                            <IndicatorRiskMetricsRow
                                                key={risk.id}
                                                metrics={normalizeRiskMetrics(risk)}
                                                name={
                                                    <Link
                                                        to={`/risk_theme/${risk.id}`}
                                                        title={risk.name}
                                                        className="flex items-center w-full min-h-[1.75rem] py-1 pl-1 pr-2 rounded-md text-[#4B4B4B] bg-[#FAFAFA] hover:bg-[#F0F0F0] transition-colors"
                                                    >
                                                        <span
                                                            className={`shrink-0 w-[0.3rem] self-stretch rounded-sm ${organizeLateralColor(risk.priority)}`}
                                                            aria-hidden
                                                        />
                                                        <span className="truncate text-[11px] leading-tight pl-1.5 flex-1 min-w-0">
                                                            {risk.name}
                                                        </span>
                                                    </Link>
                                                }
                                            />
                                        ))}
                                    </IndicatorMetricsBlock>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}
